import { CurrencyPipe, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { AuthInterceptor, MemoryOAuthStorage, ServiceOrigins, TranslatePipe, TranslationService } from '@ascent/shared';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { EshavaControlsModule } from 'eshava-controls';
import { EshavaGridModule } from 'eshava-grid';
import { ToastrModule } from 'ngx-toastr';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { environment } from './environments/environment';

// Used to be a side effect of the file that declared AscentSharedModule. With that module gone the
// registration needs a home that is loaded exactly once and before anything formats a number, and
// that is here.
registerLocaleData(localeDe, localeDeExtra);

bootstrapApplication(AppComponent, {
  providers: [
    // The bootstrap migration dropped this one silently -- the previous main.ts passed it and
    // without it every event triggers its own change detection cycle again.
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideAnimations(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // These five have no provide* function of their own, so the module is still the way to reach
    // their providers. The first three came in through AscentSharedModule; dissolving it moved them
    // here, which is also the only place a forRoot belongs.
    importProvidersFrom(
      AngularSvgIconModule.forRoot(),
      EshavaGridModule.forRoot(),
      EshavaControlsModule.forRoot(),
      // No resourceServer here any more. It could attach a token but never renew one, which was
      // enough while a hidden iframe kept the token fresh. AuthInterceptor does both.
      OAuthModule.forRoot(),
      ToastrModule.forRoot({
        maxOpened: 10,
        newestOnTop: true,
        preventDuplicates: true,
        includeTitleDuplicates: true,
        positionClass: 'toast-top-right'
      })
    ),
    // Tokens live in memory, so nothing survives a reload and no refresh token sits where a script
    // can read it. Not the library's own MemoryStorage: that one keeps the PKCE verifier in memory
    // too, and the verifier has to survive the redirect to Cardinal or no login can complete.
    { provide: OAuthStorage, useExisting: MemoryOAuthStorage },
    // The hosts the access token may be sent to -- **resource servers only**. Cardinal is not one:
    // no screen here calls an api of its, and the only requests that go there are the library's own
    // protocol ones, which set the header they need themselves.
    //
    // It was listed at first because the resourceServer option had been given both urls. That put
    // the refresh request through AuthInterceptor, where it asked for the very renewal that was
    // sending it -- see AuthenticationService.renew.
    {
      provide: ServiceOrigins,
      useValue: new ServiceOrigins([
        environment.baseUrls.ascentUrl
      ])
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // Both were declared as providers by AscentSharedModule and are injected as services, not only
    // used in templates.
    TranslatePipe,
    CurrencyPipe,
    provideAppInitializer(() => {
      const translationService = inject(TranslationService);

      return translationService.init('de');
    })
  ]
})
  .catch(err => console.error(err));
